import { useEffect, useState } from "react";
import type { JSONContent } from "@tiptap/react";

import { InternalActivity } from "@jobs/candidate-sheet-v2/comms/activity/InternalActivity";
import { InternalComments } from "./InternalComments";

import { Column } from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { commsTabTriggerClassName } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

enum InternalCommsTab {
  ACTIVITY = "ACTIVITY",
  COMMENTS = "COMMENTS",
}

// Internal scope of the comms rail: staffer-only Activity and Comments.
export const InternalCommsTabs = ({
  commentDraft,
  onCommentDraftChange,
}: {
  commentDraft: JSONContent;
  onCommentDraftChange: (contentJson: JSONContent) => void;
}) => {
  /* Context */
  const { communicationsRequestCount, communicationsTab } = useUserSheet();

  /* React Hooks */
  const [tab, setTab] = useState<InternalCommsTab>(InternalCommsTab.COMMENTS);
  // A citation asking for Comments has to move this even when the staffer had
  // switched to Activity. Keyed on the request count so clicking the same
  // citation twice still jumps, and so switching tabs by hand isn't undone.
  useEffect(() => {
    if (communicationsRequestCount === 0) return;
    if (communicationsTab !== "comments") return;
    setTab(InternalCommsTab.COMMENTS);
  }, [communicationsRequestCount, communicationsTab]);

  /* Handlers */
  const handleTabChange = (nextTab: string) => {
    setTab(nextTab as InternalCommsTab);
    mixpanelUtils.trackTabView(MIXPANEL_SCREENS.CANDIDATE_SHEET, nextTab);
  };

  /* Render */
  const render = () => {
    return (
      <Tabs
        className="flex min-h-0 w-full flex-1 flex-col overflow-hidden pt-3"
        onValueChange={handleTabChange}
        value={tab}
      >
        <Column className="w-full px-2 pb-4">
          <TabsList className="flex h-auto w-full justify-start gap-0 rounded-none border-b border-slate-300 bg-transparent p-0 text-slate-500 shadow-none">
            <TabsTrigger
              className={commsTabTriggerClassName}
              value={InternalCommsTab.ACTIVITY}
            >
              Activity
            </TabsTrigger>
            <TabsTrigger
              className={commsTabTriggerClassName}
              value={InternalCommsTab.COMMENTS}
            >
              Comments
            </TabsTrigger>
          </TabsList>
        </Column>
        <TabsContent
          className="min-h-0 w-full flex-1 overflow-y-auto px-3 pb-4"
          value={InternalCommsTab.ACTIVITY}
        >
          <InternalActivity />
        </TabsContent>
        <TabsContent
          className="min-h-0 w-full flex-1 overflow-hidden px-3 pb-4"
          value={InternalCommsTab.COMMENTS}
        >
          <InternalComments
            commentDraft={commentDraft}
            onCommentDraftChange={onCommentDraftChange}
          />
        </TabsContent>
      </Tabs>
    );
  };

  return render();
};
